import type { DerbyRngV1 } from '../rng';
import { sustainableTapRate } from './params';
import type { AiExecution, AiPolicyId, HorseParams, LaneState } from './types';

/**
 * AI riders are honest simulations: each lane gets its own RNG fork at
 * setup, reads only its own lane state, and never rubber-bands off the
 * player. A policy is a target stamina curve over race progress — tap
 * while the tank is above the curve — plus a cadence cap set by league
 * execution quality.
 */
export interface AiController {
  /** Decide whether to tap this tick. `tS` is seconds since the gun. */
  maybeTap(tS: number, lane: LaneState): boolean;
}

/** Target stamina fraction at progress p — spend down to the line. */
function targetCurve(policy: AiPolicyId, p: number): number {
  switch (policy) {
    case 'pacer':
      // Spend the tank evenly, arrive near empty.
      return 0.95 * (1 - p);
    case 'sprinter':
      // Front-load hard, fade late.
      return p < 0.5 ? 1 - 1.7 * p : Math.max(0.02, 0.15 - 0.26 * (p - 0.5));
    case 'closer':
      // Hoard until the turn for home, then empty it.
      return p < 0.7 ? 0.85 : Math.max(0.02, 0.85 * (1 - (p - 0.7) / 0.28));
    case 'lazy':
      return 0;
  }
}

export function createAi(
  params: HorseParams,
  policy: AiPolicyId,
  exec: AiExecution,
  distanceM: number,
  fork: DerbyRngV1,
): AiController {
  const sustainable = sustainableTapRate(params);
  // Lazy lanes are filler: a fixed amble regardless of the horse under them.
  const cadence = policy === 'lazy' ? 0.5 : sustainable * exec.cadenceMul;
  const baseIntervalS = 1 / cadence;
  let nextTapAtS = 0;

  return {
    maybeTap(tS: number, lane: LaneState): boolean {
      if (lane.exhausted || lane.finishTick !== null) return false;
      if (tS < nextTapAtS) return false;
      const p = Math.min(1, Math.max(0, lane.x / distanceM));
      const staminaFraction = lane.stamina / params.staminaMax;
      if (policy !== 'lazy' && staminaFraction <= targetCurve(policy, p)) {
        // Below the curve: hold, but re-check soon rather than a full cooldown.
        nextTapAtS = tS + baseIntervalS * 0.35;
        return false;
      }
      // Closers surge once the front door is open.
      const surge = policy === 'closer' && p >= 0.7 ? 0.8 : 1;
      nextTapAtS = tS + baseIntervalS * surge * (1 + fork.range(-exec.jitter, exec.jitter));
      return true;
    },
  };
}
